import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import AuthImage from "../components/AuthImage";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Plus, FolderSimple, Globe, Lock, Stack } from "@phosphor-icons/react";

function CollectionCard({ c, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.04 }}
            data-testid={`collection-card-${c.id}`}
        >
            <Link
                to={`/collections/${c.id}`}
                className="block bg-white border border-stone-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(153,0,0,0.08)] hover:border-[#990000]/20 transition-all duration-300 group"
            >
                <div className="aspect-[16/10] bg-gradient-to-br from-[#FFF4CC] via-[#FFE680]/40 to-[#990000]/10 relative overflow-hidden">
                    {c.cover_image ? (
                        <AuthImage src={c.cover_image} alt={c.title} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <Stack size={48} className="text-[#990000]/30" weight="regular" />
                        </div>
                    )}
                    <div className="absolute top-3 right-3">
                        {c.is_public ? (
                            <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-stone-700 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                                <Globe size={10} weight="bold" /> Public
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 bg-[#990000]/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full">
                                <Lock size={10} weight="bold" /> Private
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold text-[#0A0A0A] leading-snug group-hover:text-[#990000] transition-colors mb-1.5 line-clamp-1">
                        {c.title}
                    </h3>
                    {c.description && (
                        <p className="text-xs text-stone-500 line-clamp-2 mb-3">{c.description}</p>
                    )}
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-stone-500">by <span className="font-medium text-stone-700">{c.user_name}</span></span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#990000]">{c.post_count} posts</span>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

export default function CollectionsPage() {
    const { user } = useAuth();
    const [tab, setTab] = useState("browse"); // browse | mine
    const [cols, setCols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async (which) => {
        setLoading(true);
        try {
            const params = which === "mine" ? { mine: true } : {};
            const { data } = await api.get("/collections", { params });
            setCols(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(tab); }, [tab]);

    const submit = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            await api.post("/collections", { title, description, is_public: isPublic });
            toast.success("Trip plan created");
            setTitle(""); setDescription(""); setIsPublic(true);
            setOpen(false);
            setTab("mine");
            await load("mine");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="collections-page">
            <Navbar />

            <header className="bg-gradient-to-br from-[#990000] to-[#7A0000] relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-72 h-72 rounded-full bg-[#FFCC00]/10 blur-3xl" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
                        <div>
                            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFCC00] mb-2">Trip Collections</div>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter">
                                Curated trip plans<br /> by Trojans, for Trojans
                            </h1>
                            <p className="text-white/70 mt-3 max-w-xl text-sm">
                                Bundle posts into a complete itinerary — flights, hotels, places, and things to do — and share with the USC family.
                            </p>
                        </div>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="collections-new-btn" className="bg-[#FFCC00] text-[#5C0000] hover:bg-[#E6B800] h-11 px-5 font-semibold">
                                    <Plus size={16} weight="bold" className="mr-1.5" />
                                    New Trip Plan
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[480px]" data-testid="collections-create-dialog">
                                <DialogHeader>
                                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#990000] mb-1">New Trip Plan</div>
                                    <DialogTitle className="font-heading text-2xl font-semibold text-[#0A0A0A]">Create a trip collection</DialogTitle>
                                    <DialogDescription className="text-stone-500">
                                        Group related flights, hotels & activities into a single shareable plan.
                                    </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={submit} className="space-y-4 pt-2">
                                    <div>
                                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Title</Label>
                                        <Input data-testid="collections-create-title" value={title} onChange={(e) => setTitle(e.target.value)}
                                               placeholder="e.g., Kerala 7-day backpack" required maxLength={120}
                                               className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Description</Label>
                                        <Textarea data-testid="collections-create-desc" value={description} onChange={(e) => setDescription(e.target.value)}
                                                  placeholder="What's the trip about?" rows={3}
                                                  className="mt-2 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" />
                                    </div>
                                    <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg p-3">
                                        <div>
                                            <div className="text-sm font-medium">Public</div>
                                            <div className="text-xs text-stone-500">{isPublic ? "Anyone signed in can browse" : "Only you can see this"}</div>
                                        </div>
                                        <Switch data-testid="collections-create-public" checked={isPublic} onCheckedChange={setIsPublic} />
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                                        <Button type="submit" disabled={creating} data-testid="collections-create-submit"
                                                className="bg-[#990000] hover:bg-[#7A0000] text-white">
                                            {creating ? "Creating…" : "Create"}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <Tabs value={tab} onValueChange={setTab} className="mb-6">
                    <TabsList className="bg-white border border-stone-200">
                        <TabsTrigger value="browse" data-testid="collections-tab-browse" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">Browse all</TabsTrigger>
                        <TabsTrigger value="mine" data-testid="collections-tab-mine" className="data-[state=active]:bg-[#990000] data-[state=active]:text-white">My trip plans</TabsTrigger>
                    </TabsList>
                </Tabs>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={`coll-skeleton-${i}`} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                                <div className="aspect-[16/10] bg-stone-100 animate-pulse" />
                                <div className="p-5 space-y-2">
                                    <div className="h-4 bg-stone-200 animate-pulse rounded" />
                                    <div className="h-3 bg-stone-100 animate-pulse rounded w-2/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : cols.length === 0 ? (
                    <div className="bg-white border border-stone-200 rounded-xl p-16 text-center">
                        <FolderSimple size={48} className="mx-auto text-[#990000]/30 mb-4" weight="regular" />
                        <div className="font-heading text-xl font-semibold text-[#0A0A0A] mb-2">
                            {tab === "mine" ? "No trip plans yet" : "Nothing here yet"}
                        </div>
                        <p className="text-stone-500 mb-5 max-w-md mx-auto text-sm">
                            {tab === "mine"
                                ? "Bundle your favourite posts into a sharable trip itinerary. Tap the folder icon on any post to start one."
                                : "Be the first Trojan to publish a public trip plan."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cols.map((c, i) => <CollectionCard key={c.id} c={c} index={i} />)}
                    </div>
                )}
            </main>
        </div>
    );
}
